import { useCallback, useEffect, useState } from 'react';

import { formatDistanceToNow } from 'date-fns';
import { useForm } from 'react-hook-form';

import { BaseSwingThreadInput as SwingThreadInput, SwingThreadButton } from '../';
import { colors } from '../../../../colors';
import { AudioPlayer } from '../../../Audio';
import { ChatTextIcon, DrawingIcon, SpeakerIcon, TrashIcon } from '../../../icons';
import { Text } from '../../../Text';

import * as css from './SwingMessage.css';

type FormValues = { message: string };

export type SwingThreadMessageDeletePayload = {
    id: number;
    threadId: number;
};

export type MessageProps = {
    threadId: number;
    id: number;
    message?: string;
    audioUrl?: string;
    createdAt: string;
    createdBy: string;
    authorName: string | null;
};

export type SwingMessageProps = MessageProps & {
    drawing?: boolean;
    isEditingMessage: boolean;
    currentUserId: string;
    toggleEditMessage: (id: number | null) => void;
    onUpsert: (props: { message: string; id?: number; threadId: number }) => void;
    onDelete: (payload: SwingThreadMessageDeletePayload) => void;
};

type MessageItemProps = Omit<SwingMessageProps, 'authorName' | 'isEditingMessage'>;

export const SwingMessage = ({ authorName, isEditingMessage, ...rest }: SwingMessageProps) => {
    return (
        <div className={css.message}>
            <h5 className={css.title}>{authorName}</h5>
            {!isEditingMessage ? <SwingMessageRead {...rest} /> : <SwingMessageEdit {...rest} />}
        </div>
    );
};

export const SwingMessageEdit = ({
    message,
    toggleEditMessage,
    onDelete,
    onUpsert,
    id,
    threadId,
}: MessageItemProps) => {
    const {
        register,
        handleSubmit,
        reset,
        formState: { isDirty },
    } = useForm<FormValues>({
        mode: 'onChange',
        defaultValues: { message },
    });

    const onSubmit = useCallback(
        (data: FormValues) => {
            onUpsert({ id, message: data.message.trim(), threadId });
            reset({ message: data.message.trim() });
            toggleEditMessage(null);
        },
        [id, onUpsert, reset, threadId, toggleEditMessage],
    );

    return (
        <form className={css.form} onSubmit={handleSubmit(onSubmit)}>
            <SwingThreadInput {...register('message', { required: true })} noResize noOverflow />
            <div className={css.footer}>
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        toggleEditMessage(null);
                        onDelete({ id, threadId });
                    }}
                >
                    <TrashIcon size={24} color={colors.teal[400]} />
                </button>
                <span className={css.footerIcons}>
                    <SwingThreadButton variant="outlined" onClick={() => toggleEditMessage(null)}>
                        Cancel
                    </SwingThreadButton>
                    <SwingThreadButton disabled={!isDirty} type="submit">
                        Save
                    </SwingThreadButton>
                </span>
            </div>
        </form>
    );
};

export const SwingMessageRead = ({
    message,
    audioUrl,
    id,
    createdAt,
    drawing,
    createdBy,
    currentUserId,
    toggleEditMessage,
}: MessageItemProps) => {
    return (
        <>
            {message && <Text variant="p">{message}</Text>}
            {audioUrl && <AudioPlayer className={css.audio} audioSrc={audioUrl} />}
            <div className={css.footer}>
                <TimeSince createdAt={createdAt} />
                <span className={css.footerIcons}>
                    {drawing && <DrawingIcon />}
                    {message && <ChatTextIcon />}
                    {audioUrl && <SpeakerIcon />}
                    {currentUserId === createdBy && (
                        <button onClick={() => toggleEditMessage(id)} className={css.editButton}>
                            edit
                        </button>
                    )}
                </span>
            </div>
        </>
    );
};

const TimeSince = ({ createdAt }: Pick<MessageProps, 'createdAt'>) => {
    const [, setNow] = useState(() => Date.now());
    useEffect(() => {
        const interval = setInterval(() => {
            setNow(Date.now());
        }, 60 * 1000);

        return () => clearInterval(interval);
    }, [setNow]);
    return <span className={css.timeSince}>{formatDistanceToNow(createdAt)}</span>;
};
